import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, TrendingUp, Info, ArrowRight } from "lucide-react";
import { Card, PageHeader, Btn, Stat, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const TYPE_STYLES = {
  performance: "border-emerald-200 bg-emerald-50 text-emerald-900",
  activity: "border-slate-200 bg-white text-slate-800",
  reminder: "border-amber-200 bg-amber-50 text-amber-900",
  suggestion: "border-indigo-200 bg-indigo-50 text-indigo-900",
};

export default function Insights() {
  const [weekly, setWeekly] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    Promise.all([api.get("/insights/weekly"), api.get("/insights/analytics")])
      .then(([w, a]) => {
        setWeekly(w.data);
        setStats(a.data);
      })
      .catch((e) => {
        setWeekly(false);
        toast.error(errMessage(e));
      });

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/insights/weekly/refresh");
      setWeekly(data);
      toast.success("Insights updated.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (weekly === null) return <Card testId="insights-loading">Reading your week…</Card>;
  if (weekly === false) return <Card testId="insights-error">We couldn't load your insights right now.</Card>;

  return (
    <div className="space-y-6" data-testid="insights-page">
      <PageHeader
        title="PostMint Insights"
        body="A short read on your week: what happened, what performed and what to promote next."
        action={
          <Btn variant="outline" onClick={refresh} disabled={busy} testId="insights-refresh-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Btn>
        }
      />

      <Card testId="insights-headline-card" className="border-[#F95738]">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#B93615]">
          <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
          This week
        </p>
        <p className="mt-3 font-display text-xl font-semibold text-slate-900" data-testid="insights-headline">
          {weekly.headline}
        </p>
        {weekly.data_caveat && (
          <p className="mt-3 flex gap-2 text-sm text-slate-600" data-testid="insights-caveat">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            {weekly.data_caveat}
          </p>
        )}
      </Card>

      <div className="space-y-3" data-testid="insights-list">
        {(weekly.insights || []).map((i, idx) => (
          <div
            key={`${i.type}-${i.text}`}
            data-testid={`insight-${idx}`}
            className={`rounded-2xl border p-4 ${TYPE_STYLES[i.type] || TYPE_STYLES.activity}`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{i.type}</p>
            <p className="mt-1.5 text-sm font-medium">{i.text}</p>
            {i.based_on && <p className="mt-1.5 text-xs opacity-70">Based on: {i.based_on}</p>}
          </div>
        ))}
        {(weekly.insights || []).length === 0 && (
          <Empty testId="insights-empty" title="Nothing to report yet" body="Publish a few posts and your weekly read appears here." />
        )}
      </div>

      {(weekly.next_actions || []).length > 0 && (
        <Card testId="insights-next-actions">
          <p className="font-display text-lg font-semibold text-slate-900">What to do next</p>
          <ul className="mt-3 space-y-2">
            {weekly.next_actions.map((a) => (
              <li key={a} className="flex gap-2 text-sm text-slate-700">
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[#F95738]" aria-hidden="true" />
                {a}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/dashboard/create"
              data-testid="insights-create-link"
              className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
            >
              Create a post
            </Link>
            <Link
              to="/dashboard/campaigns"
              data-testid="insights-campaign-link"
              className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              Promote something
            </Link>
          </div>
        </Card>
      )}

      {stats && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat testId="insights-stat-posts" label="Posts This Week" value={stats.this_week.posts} />
            <Stat testId="insights-stat-reach" label="Reach This Week" value={stats.this_week.reach.toLocaleString()} />
            <Stat
              testId="insights-stat-engagement"
              label="Avg Engagement"
              value={stats.data_sufficient ? `${stats.this_week.avg_engagement}%` : "—"}
              hint={stats.data_sufficient ? "of reach" : "not enough data yet"}
            />
            <Stat testId="insights-stat-measured" label="Measured Posts" value={stats.measured_posts} />
          </div>

          {stats.notice && (
            <Card testId="insights-data-notice" className="border-amber-200 bg-amber-50">
              <p className="text-sm text-amber-900">{stats.notice}</p>
            </Card>
          )}

          {stats.simulated && (
            <Card testId="insights-simulated-notice">
              <p className="text-sm text-slate-600">
                These numbers come from simulated publishing while real platform access is being set up. Once your
                accounts are live, PostMint reads the real figures from each platform instead.
              </p>
            </Card>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card testId="insights-top-posts">
              <p className="font-display text-lg font-semibold text-slate-900">Top performing posts</p>
              {stats.top_posts.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">Nothing published yet.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {stats.top_posts.map((p) => (
                    <li key={p.content_item_id} className="rounded-xl border border-slate-200 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-900">{p.platform}</p>
                        <p className="text-sm font-semibold text-emerald-700">{p.engagement_rate}%</p>
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-600">{p.caption}</p>
                      <p className="mt-1.5 text-xs text-slate-400">
                        {p.reach.toLocaleString()} reach · {p.likes} likes · {p.saves} saves
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <div className="space-y-4">
              <Card testId="insights-by-pillar">
                <p className="font-display text-lg font-semibold text-slate-900">Content pillars</p>
                {stats.by_pillar.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">No published content to compare yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {stats.by_pillar.map((b) => (
                      <li key={b.name} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-700">{b.name}</span>
                        <span className="text-slate-500">
                          {b.posts} posts · {stats.data_sufficient ? `${b.avg_engagement}%` : "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {stats.unused_pillars.length > 0 && (
                  <p className="mt-3 text-xs text-slate-500" data-testid="insights-unused-pillars">
                    Not used yet: {stats.unused_pillars.join(", ")}
                  </p>
                )}
              </Card>

              <Card testId="insights-by-platform">
                <p className="font-display text-lg font-semibold text-slate-900">Platforms</p>
                {stats.by_platform.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">Nothing published yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {stats.by_platform.map((b) => (
                      <li key={b.name} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-700">{b.name}</span>
                        <span className="text-slate-500">
                          {b.posts} posts · {b.reach.toLocaleString()} reach
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>

          {stats.upcoming_dates.length > 0 && (
            <Card testId="insights-upcoming-dates">
              <p className="font-display text-lg font-semibold text-slate-900">Coming up</p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {stats.upcoming_dates.map((u) => (
                  <li
                    key={u.label}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                  >
                    {u.label} · in {u.in_days} days
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
